<template>
  <View
    gap="md"
    padding-inline="md"
    align="center"
    justify="space-between"
    class="picker-trigger"
    :class="{ selected }"
  >
    <View gap="sm" align="center" class="who">
      <AvatarPlaceholder v-if="icon" :icon="icon" />
      <Avatar v-else-if="selected" :user-name="avatarName ?? label" show-single-letter size="small" />
      <AvatarPlaceholder v-else :icon="placeholderIcon" />
      <Text variant="body-medium" class="label">{{ label }}</Text>
      <Text v-if="detail" variant="body-small" color="secondary" class="detail">{{ detail }}</Text>
    </View>

    <LoadingIndicator v-if="isLoading" />
    <Icon v-else name="chevron-down" class="chevron" />
  </View>
</template>

<script setup lang="ts">
import { IconName } from '../values/icons.const';
import AvatarPlaceholder from './avatar-placeholder.vue';
import Avatar from './avatar/avatar.vue';
import Icon from './icon.vue';
import LoadingIndicator from './loading-indicator.vue';
import Text from './text.vue';
import View from './view/view.vue';

/**
 * The closed face of a picker (a MenuButton trigger): the chosen person, or a placeholder with the
 * prompt. A choice that belongs to someone - a portfolio and its owner - shows the name as the label
 * and the person as `avatarName`; a choice that is no one's - "No portfolio" - shows an `icon`.
 */
withDefaults(
  defineProps<{
    label: string;
    selected: boolean;
    placeholderIcon?: IconName;
    isLoading?: boolean;
    /** Whose avatar to show when it is not the label's */
    avatarName?: string;
    /** A word after the label, in secondary type: "you", an owner's first name */
    detail?: string;
    /** An icon in place of the avatar, for a chosen value that is not a person */
    icon?: IconName;
  }>(),
  { placeholderIcon: 'user-03', isLoading: false },
);
</script>

<style scoped lang="scss">
.picker-trigger {
  height: 3rem;
  background-color: var(--t-color-neutral-white);
  border: 1px solid var(--t-color-border-primary);
  border-radius: var(--t-border-radius-sm);
  cursor: pointer;
  color: var(--t-color-neutral-500);
  transition: border-color 150ms ease-in-out;

  &.selected {
    color: var(--t-text-color-primary);
  }

  &:hover {
    border-color: var(--t-text-color-tertiary);
  }
}

// A long name is cut with an ellipsis rather than wrapping the trigger taller than its row
.who {
  flex: 1;
  min-width: 0;
}

.label {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.detail {
  flex-shrink: 0;
  white-space: nowrap;
}

.chevron {
  flex-shrink: 0;
  color: var(--t-text-color-tertiary);
}
</style>
